// Shared interaction styles for Security Operations (/soc). Theme colours are plain CSS variables, so
// no opacity modifiers (bg-x/10) - they generate nothing; use the theme's -muted / -hover tokens.
import React from "react";
import { severityLabel } from "./socQueue";

export const SEVERITY_CLASS = {
  critical: "bg-status-error-bg text-status-error",
  high: "bg-status-warning-bg text-status-warning",
  medium: "bg-status-warning-bg text-content-primary",
  low: "bg-theme-secondary text-content-secondary",
  unrated: "bg-theme-secondary text-content-muted",
};

// The dot beside each severity label, so severity reads at a glance down a column.
export const SEVERITY_DOT = {
  critical: "bg-status-error",
  high: "bg-status-warning",
  medium: "bg-yellow-500",
  low: "bg-content-muted",
  unrated: "bg-border",
};

export const SeverityBadge = ({ severity }) => (
  <span className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs font-medium ${SEVERITY_CLASS[severity] || SEVERITY_CLASS.unrated}`}>
    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${SEVERITY_DOT[severity] || SEVERITY_DOT.unrated}`} aria-hidden="true" />
    {severityLabel(severity)}
  </span>
);

export const FOCUS_RING = "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan focus-visible:ring-offset-1";

const SIZES = { sm: "px-2.5 py-1 text-xs", md: "px-3 py-1.5 text-sm" };
const BUTTON_BASE = `inline-flex items-center gap-1 rounded-md border font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${FOCUS_RING}`;

export const buttonClass = (size = "sm") =>
  `${BUTTON_BASE} ${SIZES[size]} border-border bg-theme-secondary text-content-primary hover:border-border-hover hover:bg-theme-hover`;
export const primaryButtonClass = (size = "sm") =>
  `${BUTTON_BASE} ${SIZES[size]} border-accent-cyan bg-accent-cyan-muted text-accent-cyan hover:bg-theme-hover`;

export const BUTTON = buttonClass("sm");
export const PRIMARY_BUTTON = primaryButtonClass("sm");

// A card that is itself an action: hover raises its border and shadow, the hint shows, focus is visible.
export const ACTION_CARD = `group flex h-full w-full flex-col rounded-xl border border-border bg-theme-card-solid p-4 text-left transition-[box-shadow,border-color,transform] duration-200 ease-out hover:border-accent-cyan hover:shadow-md motion-safe:hover:-translate-y-0.5 ${FOCUS_RING}`;

export const STATIC_CARD = "flex h-full flex-col rounded-xl border border-border bg-theme-card-solid p-4";

// Smooth-scrolls to an element unless the viewer asked for reduced motion (and never throws where
// scrolling or media queries aren't available, e.g. tests).
export const scrollToId = (id) => {
  if (typeof document === "undefined") return;
  const el = document.getElementById(id);
  if (!el || typeof el.scrollIntoView !== "function") return;
  const reduce = typeof window !== "undefined" && typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
};
